import { create } from "zustand";
import type {
  AgentStatus,
  ChatMessage,
  ToolCallEvent,
  ToolResultEvent,
  EditEvent,
  Conversation,
} from "../types/ai";
import { normalizeEditEvent } from "../types/ai";
import { api } from "../lib/api";
import { useAIStore } from "./ai";

interface ConversationData {
  conversation: Conversation;
  messages: ChatMessage[];
}

/** Rows from GET /api/v1/conversations (repository / SQLite / Supabase-backed API). */
interface ApiConversationRow {
  id: string;
  title: string;
  document_id?: string | null;
  project_id?: string | null;
  created_at: string;
  updated_at: string;
  messages?: ApiMessageRow[];
}

interface ApiMessageRow {
  id?: string;
  role: string;
  content: string;
  metadata?: { edits?: unknown[] } | null;
  created_at?: string;
}

function rowToConversation(row: ApiConversationRow): Conversation {
  return {
    id: row.id,
    title: row.title || "New conversation",
    documentId: row.document_id ?? undefined,
    projectId: row.project_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapServerMessages(raw: unknown[] | undefined): ChatMessage[] {
  if (!raw?.length) return [];
  return raw.map((m, i) => {
    const row = m as ApiMessageRow;
    const id = typeof row.id === "string" ? row.id : `msg-${i}`;
    const role = row.role === "assistant" ? "assistant" : "user";
    const content = String(row.content ?? "");
    const ts = row.created_at ? new Date(row.created_at).getTime() : Date.now();
    const meta = row.metadata;
    let edits: EditEvent[] | undefined;
    if (meta?.edits?.length) {
      edits = meta.edits.map((e) => normalizeEditEvent(e as Record<string, unknown>));
    }
    return { id, role, content, timestamp: ts, edits };
  });
}

interface AgentChatState {
  conversationList: ConversationData[];
  currentConversationId: string | null;
  messages: ChatMessage[];
  agentStatus: AgentStatus;
  streamingContent: string;
  streamingThinking: string;
  pendingEdits: EditEvent[];
  isOpen: boolean;
  showConversationList: boolean;
  _abortController: AbortController | null;
  /** Last document we loaded threads for (refresh when it changes). */
  activeDocumentId: string | null;
  conversationsLoaded: boolean;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  toggleConversationList: () => void;

  startNewConversation: () => void;
  switchConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  renameConversation: (id: string, title: string) => Promise<void>;
  /** Load sidebar threads + messages for the open document (persisted in API / DB). */
  refreshConversationsForDocument: (documentId: string | null, projectId: string | null) => Promise<void>;
  cancelStream: () => void;

  sendMessage: (params: {
    message: string;
    documentId?: string;
    projectId?: string;
    referencedFileIds?: string[];
    cursorLine?: number;
    selectionStart?: number;
    selectionEnd?: number;
    selectedText?: string;
    onEdit?: (edit: EditEvent) => void;
  }) => Promise<void>;

  clearPendingEdits: () => void;
  acceptEdit: (editIndex: number) => void;
  rejectEdit: (editIndex: number) => void;
}

function generateId() {
  return `conv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export const useAgentChatStore = create<AgentChatState>()((set, get) => ({
  conversationList: [],
  currentConversationId: null,
  messages: [],
  agentStatus: "idle",
  streamingContent: "",
  streamingThinking: "",
  pendingEdits: [],
  isOpen: false,
  showConversationList: false,
  _abortController: null,
  activeDocumentId: null,
  conversationsLoaded: false,

  setOpen: (open) => set({ isOpen: open }),
  toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),
  toggleConversationList: () => set((s) => ({ showConversationList: !s.showConversationList })),

  cancelStream: () => {
    const { _abortController } = get();
    if (_abortController) {
      _abortController.abort();
      set({ _abortController: null, agentStatus: "idle", streamingContent: "", streamingThinking: "" });
    }
  },

  refreshConversationsForDocument: async (documentId, projectId) => {
    if (!documentId) {
      set({
        conversationList: [],
        currentConversationId: null,
        messages: [],
        streamingContent: "",
        activeDocumentId: null,
        conversationsLoaded: false,
      });
      return;
    }
    if (get()._abortController) return;

    try {
      const qs = new URLSearchParams({ document_id: documentId, limit: "50" });
      if (projectId) qs.set("project_id", projectId);
      const rows = await api.get<ApiConversationRow[]>(`/api/v1/conversations?${qs.toString()}`);
      const list: ConversationData[] = (rows || []).map((row) => ({
        conversation: rowToConversation(row),
        messages: [],
      }));
      const ids = new Set(list.map((c) => c.conversation.id));
      const prevId = get().currentConversationId;
      const keepSelection = prevId && ids.has(prevId);

      set({
        conversationList: list,
        activeDocumentId: documentId,
        conversationsLoaded: true,
        currentConversationId: keepSelection ? prevId : null,
        messages: [],
        streamingContent: "",
      });

      if (keepSelection && prevId) {
        try {
          const detail = await api.get<ApiConversationRow & { messages?: unknown[] }>(
            `/api/v1/conversations/${prevId}`,
          );
          const msgs = mapServerMessages(detail.messages);
          set((s) => ({
            messages: msgs,
            conversationList: s.conversationList.map((c) =>
              c.conversation.id === prevId ? { ...c, messages: msgs } : c,
            ),
          }));
        } catch {
          set({ messages: [] });
        }
      }
    } catch {
      set({ conversationsLoaded: true, activeDocumentId: documentId });
    }
  },

  startNewConversation: () => {
    get().cancelStream();
    const { currentConversationId, messages, conversationList } = get();

    let updatedList = conversationList;
    if (currentConversationId && messages.length > 0) {
      const existing = conversationList.find((c) => c.conversation.id === currentConversationId);
      if (existing) {
        updatedList = conversationList.map((c) =>
          c.conversation.id === currentConversationId
            ? { ...c, messages, conversation: { ...c.conversation, updatedAt: new Date().toISOString() } }
            : c,
        );
      }
    }

    set({
      conversationList: updatedList,
      currentConversationId: null,
      messages: [],
      pendingEdits: [],
      streamingContent: "",
      showConversationList: false,
    });
  },

  switchConversation: async (id) => {
    get().cancelStream();
    const { currentConversationId, messages, conversationList } = get();

    let updatedList = conversationList;
    if (currentConversationId && messages.length > 0) {
      updatedList = conversationList.map((c) =>
        c.conversation.id === currentConversationId
          ? { ...c, messages }
          : c,
      );
    }

    const target = updatedList.find((c) => c.conversation.id === id);
    set({
      conversationList: updatedList,
      currentConversationId: id,
      messages: target?.messages || [],
      pendingEdits: [],
      streamingContent: "",
      showConversationList: false,
    });

    // If messages are empty, load them from the API (first switch since page load)
    if (!target?.messages.length) {
      try {
        const detail = await api.get<ApiConversationRow & { messages?: unknown[] }>(
          `/api/v1/conversations/${id}`,
        );
        const msgs = mapServerMessages(detail.messages);
        set((s) => ({
          messages: s.currentConversationId === id ? msgs : s.messages,
          conversationList: s.conversationList.map((c) =>
            c.conversation.id === id ? { ...c, messages: msgs } : c,
          ),
        }));
      } catch {
        // Keep whatever we have locally
      }
    }
  },

  deleteConversation: async (id) => {
    const { currentConversationId, conversationList } = get();
    const updatedList = conversationList.filter((c) => c.conversation.id !== id);
    if (currentConversationId === id) {
      set({
        conversationList: updatedList,
        currentConversationId: null,
        messages: [],
        pendingEdits: [],
        streamingContent: "",
      });
    } else {
      set({ conversationList: updatedList });
    }
    try {
      await api.delete(`/api/v1/conversations/${id}`);
    } catch {
      // Already removed from local state; ignore server errors
    }
  },

  renameConversation: async (id, title) => {
    set((s) => ({
      conversationList: s.conversationList.map((c) =>
        c.conversation.id === id
          ? { ...c, conversation: { ...c.conversation, title } }
          : c,
      ),
    }));
    try {
      await api.patch(`/api/v1/conversations/${id}`, { title });
    } catch {
      // Optimistic update already applied; ignore server errors
    }
  },

  sendMessage: async (params) => {
    let { currentConversationId, conversationList } = get();

    if (!currentConversationId) {
      const newId = generateId();
      const title = params.message.slice(0, 40) + (params.message.length > 40 ? "..." : "");
      const newConv: ConversationData = {
        conversation: {
          id: newId,
          title,
          projectId: params.projectId,
          documentId: params.documentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        messages: [],
      };
      currentConversationId = newId;
      conversationList = [newConv, ...conversationList];
      set({ currentConversationId: newId, conversationList });
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: params.message,
      timestamp: Date.now(),
    };

    set((s) => ({
      messages: [...s.messages, userMessage],
      agentStatus: "thinking",
      streamingContent: "",
      pendingEdits: [],
    }));

    const abortController = new AbortController();
    set({ _abortController: abortController });

    let receivedDone = false;
    let fullContent = "";
    let fullThinking = "";
    let lastToolCallId: string | undefined;
    const toolCalls: ToolCallEvent[] = [];
    const toolResults: ToolResultEvent[] = [];
    const edits: EditEvent[] = [];

    try {

      const { selectedProvider, selectedModel } = useAIStore.getState();
      const payload = {
        conversation_id: currentConversationId,
        document_id: params.documentId,
        project_id: params.projectId,
        referenced_file_ids: params.referencedFileIds,
        message: params.message,
        provider: selectedProvider || undefined,
        model: selectedModel || undefined,
        cursor_line: params.cursorLine,
        selection_start: params.selectionStart,
        selection_end: params.selectionEnd,
        selected_text: params.selectedText,
      };

      for await (const data of api.stream("/api/v1/ai/chat", payload, abortController.signal)) {
        try {
          const event = JSON.parse(data);

          // Backend creates a real id; keep sidebar + list in sync (client uses conv-… until this).
          if (event.event === "conversation" && event.data?.id) {
            const serverId = event.data.id as string;
            set((s) => {
              const prevId = s.currentConversationId;
              if (!prevId || prevId === serverId) {
                return { currentConversationId: serverId };
              }
              return {
                currentConversationId: serverId,
                conversationList: s.conversationList.map((c) =>
                  c.conversation.id === prevId
                    ? { ...c, conversation: { ...c.conversation, id: serverId } }
                    : c,
                ),
              };
            });
          }

          if (event.event === "status") {
            const statusMap: Record<string, AgentStatus> = {
              thinking: "thinking",
              reading: "reading",
              compiling: "compiling",
              editing: "editing",
            };
            set({ agentStatus: statusMap[event.data?.status] || "thinking" });
          }

          if (event.event === "thinking") {
            const chunk = event.data?.content || "";
            fullThinking = fullThinking ? `${fullThinking}\n\n${chunk}` : chunk;
            set({ streamingThinking: fullThinking });
          }

          if (event.event === "text") {
            fullContent += event.data?.content || "";
            set({ streamingContent: fullContent });
          }

          if (event.event === "tool_call") {
            lastToolCallId = event.data?.id as string | undefined;
            toolCalls.push(event.data as ToolCallEvent);
            const toolStatusMap: Record<string, AgentStatus> = {
              compile_latex: "compiling",
              edit_document: "editing",
              edit_lines: "editing",
              read_document: "reading",
              read_section: "reading",
              search_document: "reading",
            };
            set({ agentStatus: toolStatusMap[event.data?.tool] || "thinking" });
          }

          if (event.event === "tool_result") {
            toolResults.push(event.data as ToolResultEvent);
          }

          if (event.event === "edit") {
            const edit = normalizeEditEvent(
              event.data as Record<string, unknown>,
              lastToolCallId,
            );
            edits.push(edit);
            set((s) => ({ pendingEdits: [...s.pendingEdits, edit] }));
            params.onEdit?.(edit);
          }

          if (event.event === "done") {
            receivedDone = true;
            set({ agentStatus: "done" });
            setTimeout(() => {
              set((s) => (s.agentStatus === "done" ? { agentStatus: "idle" } : {}));
            }, 2000);
          }
        } catch {
          // Skip malformed JSON
        }
      }

      // Stream closed without `done` (network glitch, tab sleep, etc.) — avoid stuck "Thinking"
      if (!receivedDone) {
        set({ agentStatus: "idle" });
      }

      const replyText = fullContent.trim()
        ? fullContent
        : !receivedDone
          ? "No response received. The connection may have dropped — try sending your message again."
          : "";

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: replyText,
        thinking: fullThinking || undefined,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        toolResults: toolResults.length > 0 ? toolResults : undefined,
        edits: edits.length > 0 ? edits : undefined,
        timestamp: Date.now(),
      };

      const newMessages = [...get().messages, assistantMessage];
      const convId = get().currentConversationId;
      set((s) => ({
        messages: newMessages,
        streamingContent: "",
        streamingThinking: "",
        _abortController: null,
        conversationList: s.conversationList.map((c) =>
          c.conversation.id === convId
            ? { ...c, messages: newMessages, conversation: { ...c.conversation, updatedAt: new Date().toISOString() } }
            : c,
        ),
      }));
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        const partialText = fullContent.trim();
        if (partialText || fullThinking) {
          const partialMessage: ChatMessage = {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content: partialText || "(Interrupted before a response was generated)",
            thinking: fullThinking || undefined,
            toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
            toolResults: toolResults.length > 0 ? toolResults : undefined,
            edits: edits.length > 0 ? edits : undefined,
            timestamp: Date.now(),
          };
          const newMessages = [...get().messages, partialMessage];
          const convId = get().currentConversationId;
          set((s) => ({
            messages: newMessages,
            streamingContent: "",
            streamingThinking: "",
            _abortController: null,
            agentStatus: "idle",
            conversationList: s.conversationList.map((c) =>
              c.conversation.id === convId
                ? { ...c, messages: newMessages, conversation: { ...c.conversation, updatedAt: new Date().toISOString() } }
                : c,
            ),
          }));
        } else {
          set({ _abortController: null, agentStatus: "idle", streamingContent: "", streamingThinking: "" });
        }
        return;
      }
      set({ agentStatus: "idle", streamingContent: "", streamingThinking: "", _abortController: null });
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "Sorry, something went wrong. Please try again.",
        timestamp: Date.now(),
      };
      set((s) => {
        const convId = s.currentConversationId;
        const newMessages = [...s.messages, errorMessage];
        return {
          messages: newMessages,
          conversationList: convId
            ? s.conversationList.map((c) =>
                c.conversation.id === convId
                  ? {
                      ...c,
                      messages: newMessages,
                      conversation: { ...c.conversation, updatedAt: new Date().toISOString() },
                    }
                  : c,
              )
            : s.conversationList,
        };
      });
    }
  },

  clearPendingEdits: () => set({ pendingEdits: [] }),
  acceptEdit: (editIndex) => {
    set((s) => ({
      pendingEdits: s.pendingEdits.filter((_, i) => i !== editIndex),
    }));
  },
  rejectEdit: (editIndex) => {
    set((s) => ({
      pendingEdits: s.pendingEdits.filter((_, i) => i !== editIndex),
    }));
  },
}));
